<script setup lang="ts">
/**
 * FlagCallout — orange callout box that appears on flagged resident approval cards.
 * Explains why dual approval is required and shows the risk signal badges.
 *
 * Per spec §2.4 and CLAUDE.md: this is NOT an AI signal box (no teal, no AI badge).
 * The risk signal was computed by the backend rule engine, not an LLM.
 */
import RiskSignalBadges from './RiskSignalBadges.vue';

defineProps<{
  /** JSON-encoded RiskSignal[] string */
  riskSignalsJson: string | null | undefined;
  /** Optional custom note to display */
  note?: string;
}>();
</script>

<template>
  <div
    class="mx-4 mb-3.5 p-3 bg-orange-50 border border-orange-100 rounded-md flex gap-2.5 items-start"
    data-testid="flag-callout"
  >
    <!-- Warning icon -->
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      class="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>

    <!-- Content -->
    <div class="flex flex-col gap-1.5 min-w-0">
      <p class="text-caption text-fg-soft leading-relaxed">
        <strong class="text-orange-600 font-semibold">Dual-board approval required.</strong>
        {{ note ?? 'This resident has been flagged and requires sign-off from two board members before access is granted.' }}
      </p>
      <RiskSignalBadges :signals-json="riskSignalsJson" />
    </div>
  </div>
</template>
